import React, { FC, useContext } from "react";
import { MessageMiddleware, MessageMiddlewareContext } from "../../middlewares";
import { StandardMiddlewares } from "../../middlewares/standard-middleware";
import { ChatDetail, ChatMsgDetail } from "../../store/ChatAction";
import { ChatAgents } from "../../store/ChatState";
import { getTimeAgo, isAgent } from "../../utils";
import { ChatServerMessage } from "../../xapp/ChatServerMessage";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { ChatMarkdownMessage } from "../ChatMarkdownMessage/ChatMarkdownMessage";
import { ChatMessagePart } from "../ChatMessagePart";
import { ChatPermissionMessage } from "../ChatPermissionMessage";
import { ChatTextMessage } from "../ChatTextMessage/ChatTextMessage";
import { ChatScheduleWidget } from "../ChatScheduleWidget";
import { SourceLinks } from "../SourceLinks";
import { ToolCallDisplay } from "../ToolCallDisplay";
import "./ChatMessage.scss";
import { ChatConfigContext } from "../../config";

export interface ChatMessageProps {
    message: ChatMsgDetail;
    sibling?: boolean;
    agent: ChatUserInfo;
    readonly agents?: ChatAgents;
    readonly messageMiddleware?: MessageMiddleware;
    readonly middlewareContext?: MessageMiddlewareContext;
    minimizeOnClick?: () => void;
}

function getClassName(msg: ChatDetail) {
    return isAgent(msg.user.nick) ? "agent" : "visitor"
}

declare type AvaKey = keyof ChatServerMessage;

const avaKeys: readonly AvaKey[] = ["text", "html", "card", "list"]

/**
 * Chat Message Bubble with an Avatar
 * 
 * @param props 
 * @returns 
 */
const ChatMessage: FC<React.PropsWithChildren<ChatMessageProps>> = props => {

    const middleware = props.messageMiddleware || StandardMiddlewares;
    const chatConfig = useContext(ChatConfigContext);

    // console.log(`########### chatConfig: ${JSON.stringify(chatConfig, null, 2)}`);

    const ctx = props.middlewareContext;
    const user = props.agent;

    // Get hideUserInfo from the agent info based on the message sender's nick
    const agentInfo = props.agents?.[props.message.user.nick];
    const hideUserInfo = agentInfo?.hideUserInfo || false;

    function renderByType() {

        const msg = props.message.msg;

        switch (props.message.type) {
            // TODO: props actually requires it to be "chat.msg". Fix prop typing?
            case "chat.msg":

                // Here is the deal. If we have text (output speech), then text - card - list - options
                // OR card OR list only. Avatar with text bubble.

                const avaKey = avaKeys.find(key => !!msg[key]);
                const isStreaming = props.message.isStreaming;
                const wasStreamed = props.message.wasStreamed;

                // Tool calls - show progress while tools execute (before text arrives)
                const toolCalls = props.message.toolCalls;
                const hasToolCalls = toolCalls && toolCalls.length > 0;
                const debugMode = chatConfig?.env?.connection?.mcp?.debugMode ?? false;

                // Show tool progress with avatar when no text yet, or above text when text exists
                const showToolProgressWithAvatar = hasToolCalls && !msg.text;

                return (
                    <>
                        {/* Show tool progress with avatar when no text content yet */}
                        {showToolProgressWithAvatar && (
                            <ChatMessagePart
                                showAvatar={true}
                                user={user}
                                avatarPosition={chatConfig?.env?.theme?.messages?.avatarPosition}
                                hideUserInfo={hideUserInfo}
                                isStreaming={true}
                            >
                                <div className="chat-msg">
                                    <ToolCallDisplay toolCalls={toolCalls} debugMode={debugMode} />
                                </div>
                            </ChatMessagePart>
                        )}

                        {msg.text && (
                            <ChatMessagePart
                                showAvatar={avaKey === "text"}
                                user={user}
                                avatarPosition={chatConfig?.env?.theme?.messages?.avatarPosition}
                                hideUserInfo={hideUserInfo}
                                isStreaming={isStreaming || wasStreamed}
                            >
                                <ChatTextMessage
                                    message={props.message}
                                    sibling={props.sibling}
                                    user={user}
                                    toolCalls={hasToolCalls ? toolCalls : undefined}
                                    debugMode={debugMode}
                                />
                            </ChatMessagePart>
                        )}

                        {msg.html &&
                            <ChatMessagePart showAvatar={avaKey === "html"} user={user} avatarPosition={chatConfig?.env?.theme?.messages?.avatarPosition} hideUserInfo={hideUserInfo}>
                                <ChatMarkdownMessage message={props.message} sibling={props.sibling} onOpenUrl={props.middlewareContext?.openUrl} />
                            </ChatMessagePart>
                        }

                        {msg.displays && middleware && msg.displays.map((display, index) => {
                            if ((display as any).type === "ScheduleButton") {
                                return (
                                    <ChatScheduleWidget minimizeOnClick={props.minimizeOnClick} display={display} />
                                )
                            }
                            const Middleware = middleware;
                            return (
                                <Middleware key={index} msg={display} ctx={props.middlewareContext} />
                            )
                        })}

                        {msg.permissionRequest && ctx &&
                            <ChatMessagePart showAvatar={avaKey === "permissionRequest"} user={user} avatarPosition={chatConfig?.env?.theme?.messages?.avatarPosition} hideUserInfo={hideUserInfo}>
                                <ChatPermissionMessage message={props.message} sibling={props.sibling} ctx={ctx} />
                            </ChatMessagePart>
                        }
                    </>
                );

            // TODO: do file response?
            // case "chat.file":
            //     return (
            //         <div className={`chat-msg-container ${getClassName(props.message)} ${props.addClass}`}>
            //             {renderAvatar(props.agent)}
            //             <div className="chat-msg-wrapper">
            //                 <ChatMedia message={messageDetail} />
            //                 {renderOptions(props.message.msg.options)}
            //             </div>
            //         </div>
            //     );
            default:
                break;
        }

        return (<></>);

    }

    function renderTimestampAndSources() {
        const timestamp = props.message.timestamp;
        const ts = new Date(timestamp);
        const timeAgo = getTimeAgo(ts);
        const sources = props.message.msg?.sources;
        const hasSources = sources && sources.length > 0;
        const isAgentMessage = isAgent(props.message.user.nick);

        return (
            <div className={`chat-msg-footer ${isAgentMessage ? "agent" : "visitor"}`}>
                <span className="chat-msg-timestamp">{timeAgo}</span>
                {hasSources && (
                    <SourceLinks
                        sources={sources.map(s => ({ title: s.title, url: s.url, newTab: s.newTab }))}
                    />
                )}
            </div>
        );
    }

    // Don't show timestamp/sources while streaming or if message has no content yet
    const isStreaming = props.message.isStreaming;
    const hasContent = !!(props.message.msg?.text || props.message.msg?.html);
    const showFooter = !isStreaming && hasContent;

    // empty
    return (
        <div className={`chat-msg-container-wrapper ${isAgent(props.message.user.nick) ? "agent" : "visitor"} ${props.sibling ? "sibling" : ""}`}>
            <div className={`chat-msg-container ${getClassName(props.message)}`}>
                {renderByType()}
                {showFooter && renderTimestampAndSources()}
            </div>
        </div>
    );
}

export default ChatMessage;
